import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import React from 'react';

import ActionCard from '../components/action/card';
import ArticlesList from '../components/article/articles-list';
import Framework from '../components/framework';
import Breadcrumb from '../components/framework/breadcrumb';
import TableOfContent from '../components/framework/table-of-content';
import MarkerThumb from '../components/test/marker-thumb';
import ProductList from '../components/test/product-list';
import Trans from '../components/translation/trans';
import useTranslation from '../hooks/use_translation';
import { getVariantByLanguage, handleTableOfContent } from '../utils/helpers';

const AllergenItem = ({ data, location }) => {
	const marker = data.marker;
	const { language, t } = useTranslation('template_allergen');
	const marker_id = marker.marker_id;
	const variant = getVariantByLanguage(language, marker.marker_variants);
	const table_of_content = handleTableOfContent(variant.content);
	if (table_of_content.content) {
		variant.content = table_of_content.content;
	}

	const group = data.allGroup.nodes.find(
		(item) => marker.groups.includes(item.group_id) && item.type == 'marker',
	);
	const group_id = group?.group_id ?? null;

	return (
		<Framework
			description={variant.seo_description}
			location={location}
			tags={variant.seo_keywords}
			title={variant.seo_title}
			tracking={{ content_group: 'Allergen' }}
		>
			<div className="container mt-lg-3 mt-2 px-0" id="content">
				<nav aria-label="breadcrumb">
					<Breadcrumb
						allergen={true}
						className=""
						fact={true}
						links={[
							{
								base_url: '/allergener',
								id: group_id,
								type: 'group',
							},
							{ active: true, title: variant.title },
						]}
					/>
				</nav>
			</div>

			<div className="container">
				<div className="grid justify-content-center">
					<div className="grid-intro">
						<div className="row align-items-lg-center">
							<div className="col-auto pe-0 pe-lg-2">
								<MarkerThumb
									groups={[group_id]}
									markers={[marker_id]}
									size="lg"
								/>
							</div>
							<div className="col">
								<p className="mb-0 x-small text-muted">{variant.subtitle}</p>
								<h1 className="title-large text-dark mb-1">{variant.title}</h1>
							</div>
						</div>
						<p className="lead mt-lg-5">
							<Trans defaults={variant.excerpt} t={t} />
						</p>
					</div>
					<div className="grid-side">
						<ActionCard />
						<TableOfContent items={table_of_content.items} />
					</div>
					<div className="grid-content my-2 mt-lg-0">
						<div className="article-content">
							<Trans defaults={variant.content} t={t} />
						</div>

						<ProductList
							include_markers={[marker_id]}
							tracking={{
								item_list_name: 'Products on allergen item page',
								source: 'item',
							}}
						>
							{(component) => (
								<>
									{component && (
										<>
											<h3 className="title-small mt-5">
												{t('t1', {
													marker_title: variant.title,
												})}
											</h3>
											<section id="marker-products">{component}</section>
										</>
									)}
								</>
							)}
						</ProductList>

						<ArticlesList
							include_markers={[marker_id]}
							include_types={['blog']}
							page="artiklar"
						>
							{(component) => (
								<>
									{component && (
										<section id="marker-articles">
											<div className="mt-5">
												<h3 className="title-small">
													{t('t2', {
														marker_title: variant.title,
													})}
												</h3>
											</div>
											{component}
										</section>
									)}
								</>
							)}
						</ArticlesList>
					</div>
				</div>
			</div>
		</Framework>
	);
};

AllergenItem.propTypes = {
	data: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
};

export default AllergenItem;

export const pageQuery = graphql`
	query AllergenById($id: String!) {
		allGroup {
			nodes {
				group_id
				type
			}
		}

		marker(id: { eq: $id }) {
			id
			groups
			marker_id
			marker_variants {
				content
				excerpt
				language {
					code
				}
				seo_description
				seo_keywords
				seo_title
				slug
				subtitle
				title
			}
		}

		locales: allLocale {
			...translationFields
		}
	}
`;
